import dayjs from "dayjs";

/**
 * Chilean public holidays by year.
 * Each entry is [month (1-indexed), day].
 * Includes fixed holidays + Easter-dependent (Good Friday, Holy Saturday).
 * Easter dates are hard-coded per year.
 */
const CHILEAN_HOLIDAYS: Record<number, [number, number][]> = {
  2025: [
    [1, 1], // Año Nuevo
    [4, 18], // Viernes Santo
    [4, 19], // Sábado Santo
    [5, 1], // Día del Trabajo
    [5, 21], // Glorias Navales
    [6, 20], // Día Nacional de los Pueblos Indígenas
    [6, 29], // San Pedro y San Pablo
    [7, 16], // Virgen del Carmen
    [8, 15], // Asunción de la Virgen
    [9, 18], // Independencia
    [9, 19], // Glorias del Ejército
    [10, 12], // Encuentro de Dos Mundos
    [10, 31], // Iglesias Evangélicas y Protestantes
    [11, 1], // Día de Todos los Santos
    [12, 8], // Inmaculada Concepción
    [12, 25], // Navidad
  ],
  2026: [
    [1, 1],
    [4, 3], // Viernes Santo
    [4, 4], // Sábado Santo
    [5, 1],
    [5, 21],
    [6, 21], // Pueblos Indígenas (winter solstice)
    [6, 29],
    [7, 16],
    [8, 15],
    [9, 18],
    [9, 19],
    [10, 12],
    [10, 31],
    [11, 1],
    [12, 8],
    [12, 25],
  ],
  2027: [
    [1, 1],
    [3, 26], // Viernes Santo
    [3, 27], // Sábado Santo
    [5, 1],
    [5, 21],
    [6, 21],
    [6, 29],
    [7, 16],
    [8, 15],
    [9, 18],
    [9, 19],
    [10, 12],
    [10, 31],
    [11, 1],
    [12, 8],
    [12, 25],
  ],
};

export interface WorkingDaysInfo {
  totalWorkingDays: number;
  elapsedWorkingDays: number;
  remainingWorkingDays: number;
}

function getHolidaySet(year: number): Set<string> {
  const holidays = CHILEAN_HOLIDAYS[year] || [];
  const set = new Set<string>();
  for (const [m, d] of holidays) {
    set.add(`${year}-${m}-${d}`);
  }
  return set;
}

export function isChileanHoliday(date: dayjs.Dayjs): boolean {
  const key = `${date.year()}-${date.month() + 1}-${date.date()}`;
  return getHolidaySet(date.year()).has(key);
}

export function isWorkingDay(date: dayjs.Dayjs): boolean {
  const dow = date.day();
  if (dow === 0 || dow === 6) return false; // Weekend
  return !isChileanHoliday(date);
}

export function getWorkingDaysInfo(
  year: number,
  month: number
): WorkingDaysInfo {
  const now = dayjs();
  const currentYear = now.year();
  const currentMonth = now.month() + 1;

  const daysInMonth = dayjs(
    `${year}-${String(month).padStart(2, "0")}-01`
  ).daysInMonth();
  const holidaySet = getHolidaySet(year);

  let totalWorkingDays = 0;
  let elapsedWorkingDays = 0;

  const isPastMonth =
    year < currentYear || (year === currentYear && month < currentMonth);
  const isCurrentMonth = year === currentYear && month === currentMonth;
  const todayDate = now.date();

  for (let d = 1; d <= daysInMonth; d++) {
    const date = dayjs(
      `${year}-${String(month).padStart(2, "0")}-${String(d).padStart(2, "0")}`
    );
    const dow = date.day();
    if (dow === 0 || dow === 6) continue;
    const key = `${year}-${month}-${d}`;
    if (holidaySet.has(key)) continue;

    totalWorkingDays++;

    if (isPastMonth) {
      elapsedWorkingDays++;
    } else if (isCurrentMonth && d <= todayDate) {
      elapsedWorkingDays++;
    }
  }

  return {
    totalWorkingDays,
    elapsedWorkingDays,
    remainingWorkingDays: totalWorkingDays - elapsedWorkingDays,
  };
}
